import { forwardRef, ForwardRefRenderFunction, useState, useMemo, ReactNode } from 'react'
import { MenuItem } from '@mui/material'
import { SelectInputProps } from '@mui/material/Select/SelectInput'
import { theme } from 'styles/theme/hav-theme'
import { Tooltip } from 'components'

import QuotaSelectModal from './components/QuotaSelectModal'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import * as S from './styles'
import { formatDate } from 'commons/utils/date'

export interface QuotaOptionProps {
  value: string | number
  title: string
  anticipated?: boolean
}

export interface QuotaSelectProps {
  name: string
  label?: string
  labelAdornment?: ReactNode
  value: string
  options: Array<QuotaOptionProps>
  disabled?: boolean
  onChange: SelectInputProps<unknown>['onChange']
  onBlur?: SelectInputProps<unknown>['onBlur']
}

const menuStyle = {
  padding: theme.spacing.sm,
  gap: theme.spacing.sm,
  '& ul': {
    padding: 0
  },
  '& li': {
    borderRadius: theme.shapes.borderRadius.sm,
    border: `1px solid ${theme.colors.quartenary}`,
    ':not(:last-child)': {
      marginBottom: theme.spacing.xs
    }
  }
}

const QuotaSelectComponent: ForwardRefRenderFunction<
  HTMLInputElement,
  QuotaSelectProps
> = (
  { label, name, value, options, disabled = false, onChange, onBlur, labelAdornment },
  ref
) => {
  const [openRegularModal, setOpenRegularModal] = useState(false)
  const [openAnticipatedModal, setOpenAnticipatedModal] = useState(false)

  const handleQuotaSelection = (option: QuotaOptionProps) => {
    const { anticipated } = option
    const currentOption = options.find((element) => element.value === value)

    if (anticipated && !currentOption?.anticipated) {
      setOpenAnticipatedModal(true)
    } else if (!anticipated && currentOption?.anticipated) {
      setOpenRegularModal(true)
    }
  }

  const hasInactiveValue = useMemo(
    () => !!value && !options.find((option) => option.value === value),
    [value]
  )

  return (
    <S.FormControlStyled variant="outlined" fullWidth>
      {!!label && (
        <S.WrapLabelStyled>
          <S.LabelStyled id="labelQuotaSelect">{label}</S.LabelStyled>
          {labelAdornment && labelAdornment}
        </S.WrapLabelStyled>
      )}

      <S.SelectStyled
        height={30}
        name={name}
        inputRef={ref}
        value={value}
        displayEmpty
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        IconComponent={ExpandMoreIcon}
        MenuProps={{
          PaperProps: {
            sx: menuStyle
          }
        }}
      >
        {!value && (
          <MenuItem key={'empty-option'} value="" disabled>
            <S.ItemWrapper>
              <S.ItemLabel>{'Selecione um cliente'}</S.ItemLabel>
            </S.ItemWrapper>
          </MenuItem>
        )}
        {hasInactiveValue && (
          <MenuItem key={'disabled-option'} value={value} disabled>
            <S.ItemWrapper>
              <S.ItemLabel>{formatDate(value, 'MM/YYYY')}</S.ItemLabel>
            </S.ItemWrapper>
          </MenuItem>
        )}
        {options.map((option, key) => {
          return (
            <MenuItem
              key={key}
              value={option.value}
              onClick={() => handleQuotaSelection(option)}
            >
              <S.ItemWrapper>
                <S.ItemLabel>{option.title}</S.ItemLabel>
                {option.anticipated && (
                  <Tooltip
                    arrow
                    placement="bottom-start"
                    title="O pedido será efetivado automaticamente dentro do mês cota selecionado após análise das áreas produtivas."
                  >
                    <S.SelectTag>
                      <span>Pré-Venda</span>
                    </S.SelectTag>
                  </Tooltip>
                )}
              </S.ItemWrapper>
            </MenuItem>
          )
        })}
      </S.SelectStyled>

      <QuotaSelectModal
        title="Você mudou para pedido padrão!"
        description="O pedido padrão impacta, diretamente, na cota do mês selecionado, e deve cumprir os critérios de cota e estoque para envio ao SAP."
        open={openRegularModal}
        setOpen={setOpenRegularModal}
      />
      <QuotaSelectModal
        title="Você mudou para pré-venda!"
        description="A pré-venda servirá como uma projeção para sua cota. Ele será enviado ao SAP, somente, no mês selecionado, impactando a sua cota futura."
        open={openAnticipatedModal}
        setOpen={setOpenAnticipatedModal}
      />
    </S.FormControlStyled>
  )
}

export const QuotaSelect = forwardRef(QuotaSelectComponent)
